import { createContext, useContext, useState, useEffect, type ReactNode } from "react";

export type StreamTier = "personal" | "pro" | "enterprise";

export interface StreamContextValue {
  stream: StreamTier | null;
  setStream: (tier: StreamTier | null) => void;
  isLoaded: boolean;
  /** Human-readable tier label */
  streamLabel: string;
}

const defaultContext: StreamContextValue = {
  stream: null,
  setStream: () => {},
  isLoaded: false,
  streamLabel: "",
};

export const StreamContext = createContext<StreamContextValue>(defaultContext);

const STREAM_STORAGE_KEY = "synqus_selected_stream";

/** Maps each stream to the persona IDs it should show */
export const STREAM_PERSONA_MAP: Record<StreamTier, string[]> = {
  personal: ["personal", "fitness"],
  pro: ["freelancer", "tutor", "creator", "sales", "healthcare", "realestate", "beauty", "fitness"],
  enterprise: ["enterprise", "sales", "healthcare"],
};

const STREAM_LABELS: Record<StreamTier, string> = {
  personal: "Personal",
  pro: "Business",
  enterprise: "Enterprise",
};

export function StreamProvider({ children }: { children: ReactNode }) {
  const [stream, setStreamState] = useState<StreamTier | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(STREAM_STORAGE_KEY) as StreamTier | null;
    if (stored && ["personal", "pro", "enterprise"].includes(stored)) {
      setStreamState(stored);
    }
    setIsLoaded(true);
  }, []);

  const handleSetStream = (tier: StreamTier | null) => {
    setStreamState(tier);
    if (tier) {
      localStorage.setItem(STREAM_STORAGE_KEY, tier);
    } else {
      localStorage.removeItem(STREAM_STORAGE_KEY);
    }
  };

  return (
    <StreamContext.Provider
      value={{
        stream,
        setStream: handleSetStream,
        isLoaded,
        streamLabel: stream ? STREAM_LABELS[stream] : "",
      }}
    >
      {children}
    </StreamContext.Provider>
  );
}

export function useStream(): StreamContextValue {
  const ctx = useContext(StreamContext);
  if (!ctx) throw new Error("useStream must be used within StreamProvider");
  return ctx;
}
